<template>
  <page-loader class="pb-10">
    <v-container>
      <v-row>
        <v-col class="d-flex">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
        </v-col>
        <v-col class="text-right">
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Click the FAB in the bottom right then click "Save Page Type" to keep your changes!</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-container v-if="pageErrors.length">
      <v-row>
        <v-col>
          <v-alert
            v-for="({ message }, index) in pageErrors"
            :key="message"
            :class="{ 'mt-2': index !== 0 }"
            class="mb-0"
            type="error"
            dense
          >
            {{ message }}
          </v-alert>
        </v-col>
      </v-row>
    </v-container>

    <v-form ref="form">
      <v-container>
        <v-row>
          <v-col sm="4" class="order-sm-2">
            <v-row>
              <v-col>
                <v-card outlined>
                  <v-card-subtitle class="mb-6 grey lighten-4 pa-3">Details</v-card-subtitle>
                  <v-card-text>
                    <text-input v-model="formData.name" disabled label="Name" />
                    <text-input
                      v-if="formData.pageTypeGroup"
                      v-model="formData.pageTypeGroup.name"
                      disabled
                      label="Page Type Group"
                    />
                    <select-list
                      v-model="formData.primary_offer_type"
                      :items="getItemsForOption('offerTypes')"
                      label="Primary Offer Type"
                    />
                    <select-list
                      v-model="formData.schema.layout"
                      :items="layouts"
                      :disabled="isLoading"
                      :loading="isLoading"
                      class="mb-4"
                      label="Layout"
                      hide-details
                      @change="onLayoutChange"
                    />
                    <toggle-input v-model="formData.is_ready" class="mb-4" label="Ready for content" />
                    <toggle-input v-model="formData.has_hub" class="mb-4" label="Has Hub?" />
                  </v-card-text>

                  <v-card-actions class="justify-center">
                    <router-link
                      class="text-caption"
                      :to="{ name: 'content.pages', query: { page_type_id: $route.params.id } }"
                    >
                      Total Pages: {{ pageTotal }}
                    </router-link>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-card outlined>
                  <v-card-subtitle class="grey lighten-4 pa-3">Entity Types</v-card-subtitle>
                  <v-list class="py-0">
                    <v-list-item
                      v-for="(entityTypePageType, index) in entityTypePageTypes.data"
                      :key="entityTypePageType.id"
                      :class="{ 'grey lighten-4': index % 2 !== 0 }"
                      class="d-flex"
                      dense
                    >
                      <div class="flex-1 body-2 py-2">
                        <div>{{ entityTypePageType.entityType.name }}</div>
                        <base-chip
                          v-if="entityTypePageType.label"
                          :text="entityTypePageType.label"
                          icon="label"
                          small
                        />
                      </div>
                      <v-spacer />
                      <more-menu small>
                        <v-list-item
                          :disabled="!isPermittedTo('update')"
                          :inactive="!isPermittedTo('update')"
                          @click="
                            $router.push({
                              name: 'entity-types.view',
                              params: { id: entityTypePageType.entity_type_id },
                            })
                          "
                        >
                          <v-list-item-title>View</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          :disabled="!isPermittedTo('update')"
                          :inactive="!isPermittedTo('update')"
                          @click="onRenameEntityType(entityTypePageType)"
                        >
                          <v-list-item-title>{{
                            entityTypePageType.label ? 'Update Label' : 'Add Label'
                          }}</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          :disabled="!isPermittedTo('delete') || !hasRole('admin')"
                          :inactive="!isPermittedTo('delete') || !hasRole('admin')"
                          @click="onDetachEntityType(entityTypePageType)"
                        >
                          <v-list-item-title>Remove</v-list-item-title>
                        </v-list-item>
                      </more-menu>
                    </v-list-item>
                  </v-list>
                  <v-card-actions class="grey lighten-4 d-block">
                    <select-list
                      v-model="attach.entity_type_id"
                      :items="entityTypeOptions"
                      :disabled="isLoading"
                      :loading="isLoading"
                      label="Entity Type"
                      class="mb-2 white"
                      clearable
                      hide-details
                    />

                    <v-btn
                      :disabled="!attachableEntityType"
                      color="primary"
                      depressed
                      block
                      @click="showAttachEntityTypeModal = true"
                    >
                      Add Entity Type
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-card outlined>
                  <v-card-subtitle class="grey lighten-4 pa-3">Site Groups</v-card-subtitle>
                  <v-list class="py-0">
                    <v-list-item
                      v-for="(pageTypeSiteGroup, index) in pageTypeSiteGroups.data"
                      :key="pageTypeSiteGroup.id"
                      :class="{ 'grey lighten-4': index % 2 !== 0 }"
                      class="d-flex"
                      dense
                    >
                      <div v-if="pageTypeSiteGroup.siteGroup" class="body-2">
                        {{ pageTypeSiteGroup.siteGroup.name }}
                      </div>
                      <v-spacer />
                      <more-menu small>
                        <v-list-item
                          :disabled="!isPermittedTo('update')"
                          :inactive="!isPermittedTo('update')"
                          @click="
                            $router.push({
                              name: 'site-groups.management',
                              query: { searchTerm: pageTypeSiteGroup.siteGroup.name },
                            })
                          "
                        >
                          <v-list-item-title>View</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                          :disabled="!isPermittedTo('delete')"
                          :inactive="!isPermittedTo('delete')"
                          @click="onDetachSiteGroup(pageTypeSiteGroup)"
                        >
                          <v-list-item-title>Remove</v-list-item-title>
                        </v-list-item>
                      </more-menu>
                    </v-list-item>
                  </v-list>

                  <v-card-actions class="grey lighten-4 d-block">
                    <select-list
                      v-model="attach.site_group_id"
                      :items="siteGroupOptions"
                      :disabled="isLoading"
                      :loading="isLoading"
                      label="Site Group"
                      class="mb-2 white"
                      clearable
                      hide-details
                    />

                    <v-btn
                      :disabled="!attachableSiteGroup"
                      color="primary"
                      depressed
                      block
                      @click="showAttachSiteGroupModal = true"
                    >
                      Add Site Group
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
          <v-col sm="8" class="order-sm-1">
            <v-card class="mb-4" outlined>
              <v-tabs v-model="currentTab" grow show-arrows>
                <v-tab v-for="(sectionModules, section) in formData.schema.sections" :key="section">
                  {{ section }} Layout
                </v-tab>
                <v-tab>Template Options</v-tab>
                <v-tab>Offer Configuration</v-tab>
              </v-tabs>
            </v-card>

            <v-tabs-items v-model="currentTab" class="transparent">
              <v-tab-item v-for="(sectionModules, section) in formData.schema.sections" :key="section">
                <module-block-group
                  v-model="formData.schema.sections[section]"
                  :section="section"
                  :sections="formData.schema.sections"
                  :template-options="formData.template_options"
                  :modules="modules"
                  :is-loading="isLoading"
                  :readonly="!isPermittedTo('update')"
                />
              </v-tab-item>

              <v-tab-item>
                <template-option-block-group
                  v-model="formData.template_options"
                  :original-template-options="original.template_options"
                  :is-loading="isLoading"
                  :readonly="!isPermittedTo('update')"
                />
              </v-tab-item>

              <v-tab-item eager>
                <offer-configuration
                  ref="offer-configuration"
                  v-model="formData.offer_config"
                  :entity-types="offerConfigEntityTypes"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Page Type"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
      @delete="showDeleteModal = true"
    />

    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="pageType"
      @modal-confirm="onResetConfirm"
    />

    <!-- Site Group Detach Modal -->
    <generic-modal
      v-if="detachableSiteGroup"
      v-model="showDetachSiteGroupModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Add Site Group"
      hide-louis
      persistent
      @modal-confirm="onDetachSiteGroupConfirm"
      @modal-cancel="showDetachSiteGroupModal = false"
    >
      <span
        >Are you sure you want to remove <span class="font-weight-bold">{{ detachableSiteGroup.name }}</span
        >?</span
      >
    </generic-modal>

    <!-- Entity Type Update (Label) Modal -->
    <generic-modal
      v-model="showEntityTypeUpdateModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Apply Entity Type Label"
      hide-louis
      persistent
      @modal-confirm="onEntityTypeUpdateConfirm"
      @modal-cancel="onEntityTypeUpdateCancel"
    >
      <p>
        Adding a label can help distinguish Entity Types from each other when setting up new pages and configuring
        offers in Template Management.
      </p>
      <template v-if="entityTypeToUpdate">
        <div class="mb-4 body-1">{{ entityTypeToUpdate.entityType.name }}</div>
        <text-input v-model="entityTypeToUpdate.label" label="Label" />
      </template>
    </generic-modal>

    <!-- Entity Type Detach Modal -->
    <generic-modal
      v-model="showDetachEntityTypeModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Remove Entity Type"
      hide-louis
      persistent
      @modal-confirm="onDetachEntityTypeConfirm"
      @modal-cancel="showDetachEntityTypeModal = false"
    >
      <div v-if="detach && detach.entityType">
        Are you sure you want to remove <span class="font-weight-bold">{{ detach.entityType.name }}</span
        >{{ detach.label ? ` (${detach.label})` : '' }}?
      </div>
      <div class="mt-1">If this Entity Type is being used in Offer Configuration, this may cause issues.</div>
    </generic-modal>

    <!-- Site Group Attach Modal -->
    <generic-modal
      v-if="attachableSiteGroup"
      v-model="showAttachSiteGroupModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Add Site Group"
      hide-louis
      persistent
      @modal-confirm="onAttachSiteGroupConfirm"
      @modal-cancel="showAttachSiteGroupModal = false"
    >
      <span
        >Are you sure you want to add <span class="font-weight-bold">{{ attachableSiteGroup.name }}</span
        >?</span
      >
    </generic-modal>

    <!-- Entity Type Attach Modal -->
    <generic-modal
      v-if="attachableEntityType"
      v-model="showAttachEntityTypeModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Add Entity Type"
      hide-louis
      persistent
      @modal-confirm="onAttachEntityTypeConfirm"
      @modal-cancel="showAttachEntityTypeModal = false"
    >
      <div>
        Are you sure you want to add <span class="font-weight-bold">{{ attachableEntityType.name }}</span
        >?
      </div>
      <div>This will be actioned immediately and cannot be undone.</div>
    </generic-modal>

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Page Type"
      hide-louis
      persistent
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <span>Are you sure you want to save these changes?</span>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Page Type"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this page type? This cannot be
        undone.
      </div>
    </generic-modal>

    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters, mapMutations } from 'vuex'
// Services
import { pageTypes, siteGroups, entityTypes, pages, modules } from '@/services/offers'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import RedirectHandler from '@/mixins/redirect-handler'
// Components
import TemplateOptionBlockGroup from './components/template-option-block-group'
import ModuleBlockGroup from './components/module-block-group'
import MoreMenu from '@/components/more-menu'
import SelectList from '@/components/forms/select-list'
import BaseChip from '@/components/chips/base-chip'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import OfferConfiguration from '@/views/page-types/components/offer-configuration'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import TextInput from '@/components/forms/text-input'
import ToggleInput from '@/components/forms/toggle-input'

const blankFormData = () => ({
  name: null,
  core_entities: [],
  has_hub: false,
  is_ready: false,
  page_type_group_id: null,
  primary_offer_type: null,
  schema: { layout: 'main', sections: { main: [] } },
  template_options: [],
  offer_config: {
    offerType: null,
    filters: [],
  },
})

const blankAttachAndDetach = () => ({
  id: null,
  entity_type_id: null,
  site_group_id: null,
})

export default {
  name: 'PageTypeEdit',
  components: {
    TemplateOptionBlockGroup,
    ModuleBlockGroup,
    SelectList,
    BaseChip,
    ExitModal,
    FabActions,
    MoreMenu,
    GenericModal,
    OfferConfiguration,
    PageLoader,
    ResetModal,
    ToggleInput,
    TextInput,
  },
  mixins: [DataModification, PermissionsHandler, RedirectHandler],
  data() {
    return {
      isLoading: false,
      currentTab: null,
      attach: blankAttachAndDetach(),
      detach: blankAttachAndDetach(),
      formData: blankFormData(),
      original: blankFormData(),
      siteGroups: [],
      entityTypeToUpdate: null,
      entityTypes: [],
      modules: [],
      showDeleteModal: false,
      showResetModal: false,
      showSaveModal: false,
      showAttachSiteGroupModal: false,
      showAttachEntityTypeModal: false,
      showDetachSiteGroupModal: false,
      showDetachEntityTypeModal: false,
      showEntityTypeUpdateModal: false,
      pageTotal: 0,
    }
  },
  computed: {
    ...mapGetters({
      entityTypePageTypes: 'entityTypePageTypes/getEntityTypePageTypes',
      pageTypeSiteGroups: 'pageTypeSiteGroups/getPageTypeSiteGroups',
      getItemsForOption: 'options/getItemsForOption',
    }),
    attachableSiteGroup() {
      return this.siteGroups.find(({ id }) => id === this.attach.site_group_id)
    },
    attachableEntityType() {
      return this.entityTypes.find(({ id }) => id === this.attach.entity_type_id)
    },
    detachableSiteGroup() {
      return this.siteGroups.find(({ id }) => id === this.detach.site_group_id)
    },
    siteGroupOptions() {
      return this.siteGroups.map(({ name: key, id: value }) => ({
        key,
        value,
        disabled: !!this.pageTypeSiteGroups.data.some(({ site_group_id }) => site_group_id === value),
      }))
    },
    entityTypeOptions() {
      return this.entityTypes.map(({ name: key, id: value }) => ({ key, value }))
    },
    layouts() {
      return this.getItemsForOption('pageTypeLayouts')
    },
    hasChanges() {
      return this.hasDataChanged(this.formData, this.original)
    },
    breadcrumbs() {
      return [
        { text: 'Template Management', to: { name: 'page-types.management' }, exact: true },
        { text: this.original.name, disabled: true },
      ]
    },
    offerConfigEntityTypes() {
      try {
        return this.entityTypePageTypes.data.map(({ label, id, entityType: { name } = {} }) => ({
          key: label || name,
          value: id,
        }))
      } catch (e) {
        console.warn('Unable to get Entity Types for Offer Config')
        return []
      }
    },
    pageErrors() {
      return [
        {
          message: 'Missing Entity Type in Offer Configuration',
          error: this.formData?.offer_config?.filters.find(({ entityType }) => {
            if (!entityType) return false
            if (Array.isArray(entityType)) {
              return entityType.find(
                entityTypeId => !this.entityTypePageTypes.data.find(({ id }) => id === entityTypeId)
              )
            }
            return !this.entityTypePageTypes.data.find(({ id }) => id === entityType)
          }),
        },
      ].filter(({ error }) => !!error)
    },
  },
  async created() {
    await Promise.all([
      this.getPageType(),
      this.getPages(),
      this.getEntityTypes(),
      this.getSiteGroups(),
      this.getModules(),
      this.fetchPageTypeSiteGroups({ filters: { page_type_id: this.$route.params.id }, paginate: { perPage: null } }),
      this.fetchEntityTypePageTypes({ filters: { page_type_id: this.$route.params.id }, paginate: { perPage: null } }),
    ])

    this.setPageLoading(false)
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapActions({
      fetchPageTypeSiteGroups: 'pageTypeSiteGroups/fetch',
      fetchEntityTypePageTypes: 'entityTypePageTypes/fetch',
      attachEntityTypePageTypes: 'entityTypePageTypes/attach',
      attachPageTypeSiteGroups: 'pageTypeSiteGroups/attach',
      deleteEntityTypePageTypes: 'entityTypePageTypes/detach',
      deletePageTypeSiteGroups: 'pageTypeSiteGroups/detach',
      updateEntityTypePageTypes: 'entityTypePageTypes/update',
    }),
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    onLayoutChange() {
      const selectedLayout = this.layouts.find(layout => layout.value === this.formData.schema.layout)
      this.formData.schema.sections = selectedLayout.sections.reduce(
        (a, b) => ((a[b] = this.formData.schema.sections[b] || []), a),
        {}
      )
    },
    async getPages() {
      this.isLoading = true
      try {
        const { pagination } = await pages.getAll({
          filters: { page_type_id: this.$route.params.id },
          paginate: { perPage: 0 },
        })
        this.pageTotal = pagination.total
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve pages',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'page-types.management' })
      }
    },
    async getPageType() {
      this.isLoading = true
      try {
        this.formData = await pageTypes.get(this.$route.params.id)
        this.original = cloneDeep(this.formData)
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve page type',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'page-types.management' })
      }
      this.isLoading = false
    },
    async getModules() {
      this.isLoading = true
      try {
        const { data } = await modules.getAll({})
        this.modules = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve site groups',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'page-types.management' })
      }
    },
    async getSiteGroups() {
      this.isLoading = true
      try {
        const { data } = await siteGroups.getAll({})
        this.siteGroups = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve site groups',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'page-types.management' })
      }
    },
    async getEntityTypes() {
      this.isLoading = true
      try {
        const { data } = await entityTypes.getEntityTypes({})
        this.entityTypes = data
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve entity types',
          data: { error },
          duration: -1,
        })
        this.$_redirectHandler_404({ name: 'page-types.management' })
      }
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await pageTypes.delete(this.formData.id)
        this.$notify({
          type: 'success',
          text: `Successfully removed page type '${this.original.name}'`,
        })
        this.$router.push({ name: 'page-types.management' })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to delete page type',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onResetConfirm() {
      this.formData = cloneDeep(this.original)
      this.showResetModal = false
    },
    onDetachSiteGroup({ site_group_id }) {
      this.detach.site_group_id = site_group_id
      this.showDetachSiteGroupModal = true
    },
    async onDetachSiteGroupConfirm() {
      this.isLoading = true
      try {
        await this.deletePageTypeSiteGroups({
          page_type_id: this.formData.id,
          site_group_id: this.detach.site_group_id,
        })

        this.detach.site_group_id = null
        this.showDetachSiteGroupModal = false
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to remove site group',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async onAttachSiteGroupConfirm() {
      this.isLoading = true
      try {
        await this.attachPageTypeSiteGroups({
          page_type_id: this.formData.id,
          site_group_id: this.attach.site_group_id,
        })

        this.attach.site_group_id = null
        this.showAttachSiteGroupModal = false
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to add site group',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onDetachEntityType(entityTypePageType) {
      this.detach = entityTypePageType
      this.showDetachEntityTypeModal = true
    },
    async onDetachEntityTypeConfirm() {
      this.isLoading = true
      try {
        await this.deleteEntityTypePageTypes({ id: this.detach.id })

        this.showDetachEntityTypeModal = false
        this.detach = blankAttachAndDetach()
        this.$notify({
          type: 'success',
          text: 'Succesfully removed Entity Type from Template',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to remove entity type',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async onAttachEntityTypeConfirm() {
      this.isLoading = true
      try {
        await this.attachEntityTypePageTypes({
          page_type_id: this.formData.id,
          entity_type_id: this.attach.entity_type_id,
        })

        this.showAttachEntityTypeModal = false
        this.attach = blankAttachAndDetach()
        this.$notify({
          type: 'success',
          text: 'Successfully added Entity Type to Template',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to add Entity Type',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    async onEntityTypeUpdateConfirm() {
      try {
        this.isLoading = true
        await this.updateEntityTypePageTypes(this.entityTypeToUpdate)
        this.showEntityTypeUpdateModal = false
        this.entityTypeToUpdate = null
        this.$notify({
          type: 'success',
          text: 'Successfully updated Entity Type label',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to update Entity Type',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    onEntityTypeUpdateCancel() {
      this.showEntityTypeUpdateModal = false
      this.entityTypeToUpdate = null
    },
    onRenameEntityType(entityType) {
      this.entityTypeToUpdate = { ...entityType }
      this.showEntityTypeUpdateModal = true
    },
    async onSaveConfirm() {
      try {
        if (!this.$refs.form.validate() || !this.$refs['offer-configuration'].$refs.form.validate())
          throw 'Missing required fields'

        this.isLoading = true

        await pageTypes.update(this.formData.id, this.formData)
        this.original = cloneDeep(this.formData)
        this.$notify({
          type: 'success',
          text: 'Successfully saved page type',
        })

        this.showSaveModal = false
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save page type',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
  },
}
</script>
